"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, CircleCheck as CheckCircle2, Clock, Globe as Globe2, Loader as Loader2, Mail, MapPin, PhoneCall, Send, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface ContactFeature {
  id: number;
  title: string;
  description: string;
}
const contactFeatures: ContactFeature[] = [{
  id: 1,
  title: "Application Feasibility",
  description: "Method suitability reviews, sample feasibility runs, and optical bench testing."
}, {
  id: 2,
  title: "Qualification & Compliance",
  description: "Full IQ/OQ/PQ execution documentation for regulated GMP/GLP environments."
}, {
  id: 3,
  title: "Territory Support",
  description: "Direct field service engineering and applications support across Ireland and the UK."
}];
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-secondary/50 rounded-lg border border-border">
        <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Inquiry Received</h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6 leading-relaxed">
          {successMessage || "Thank you for reaching out. Our analytical applications team will review your requirements and respond within one business day."}
        </p>
        <Button type="button" variant="outline" onClick={resetForm} className="border-border text-foreground hover:bg-muted">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Please provide at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" autoComplete="name" className="bg-background border-input text-foreground h-10 px-3.5" {...field} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid business email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Work Email <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" autoComplete="email" className="bg-background border-input text-foreground h-10 px-3.5" {...field} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Company Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" autoComplete="organization" className="bg-background border-input text-foreground h-10 px-3.5" {...field} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" autoComplete="tel" className="bg-background border-input text-foreground h-10 px-3.5" {...field} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please describe your application or inquiry",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Message <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your measurement technique of interest, sample types, testing environment, or support requirement..." rows={4} className="bg-background border-input text-foreground resize-y min-h-[110px] px-3.5 py-2.5" {...field} />
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm font-medium">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 text-base font-medium transition-all shadow-sm">
          {isSubmitting ? <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Transmitting Inquiry...
            </> : <>
              <Send className="mr-2 h-4 w-4" />
              Submit Application Inquiry
            </>}
        </Button>
        <p className="mt-2.5 text-center text-xs text-muted-foreground">
          Your project information is held in strict commercial confidence.
        </p>
      </div>
    </Form>;
}
export default function Inquiry() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="contactinquiry" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Briefing Panel (5 cols) */}
          <motion.div className="lg:col-span-5 flex flex-col justify-between space-y-8" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wide uppercase mb-4 border border-border">
                <Globe2 className="w-3.5 h-3.5 text-primary" />
                Ireland &amp; UK Application Specialists
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default leading-tight mb-4">
                Discuss Your Analytical Application
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                Connect with our technical specialists for instrument qualification, PAT process integration, or benchtop testing solutions tailored to your laboratory requirements.
              </p>
            </div>

            {/* Technical Highlights */}
            <div className="space-y-4 pt-2">
              {contactFeatures.map((feature, i) => <div key={feature.id} data-index={i} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-secondary/40 border border-border/70">
                  <div className="mt-0.5 rounded-md bg-background p-1.5 text-primary border border-border/80 shadow-xs">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Direct Contact Points */}
            <div className="p-5 rounded-xl bg-card border border-border space-y-4 shadow-xs">
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Direct Channels
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <a href="mailto:info@antechsolutions.ie" className="flex items-center gap-2.5 text-foreground hover:text-primary transition-colors py-1 group">
                  <div className="p-2 rounded-md bg-secondary text-muted-foreground group-hover:text-primary transition-colors">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div className="truncate">
                    <p className="text-xs text-muted-foreground">Inquiries</p>
                    <span className="font-medium truncate block">info@antechsolutions.ie</span>
                  </div>
                </a>

                <a href="tel:+3531800268324" className="flex items-center gap-2.5 text-foreground hover:text-primary transition-colors py-1 group">
                  <div className="p-2 rounded-md bg-secondary text-muted-foreground group-hover:text-primary transition-colors">
                    <PhoneCall className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Direct Line</p>
                    <span className="font-medium block">+353 (0)1 800 2683</span>
                  </div>
                </a>
              </div>

              <div className="pt-2 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-primary" />
                  Dublin, Ireland &amp; Manchester, UK
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                  Mon-Fri 08:30 - 17:30
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Form Card (7 cols) */}
          <motion.div className="lg:col-span-7" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }}>
            <Card className="border border-border bg-card shadow-sm rounded-xl overflow-hidden py-0">
              <CardHeader className="bg-secondary/30 border-b border-border px-6 py-6 sm:px-8">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                    Contact Us - Antech Solutions
                  </CardTitle>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20 shrink-0">
                    <Building2 className="w-3.5 h-3.5" />
                    Application Inquiry
                  </span>
                </div>
                <CardDescription className="text-sm text-muted-foreground mt-1.5">
                  Submit your technical request, instrument inquiry, or validation support scope below.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6 sm:p-8">
                <FormProvider formId="contact_form" sectionName="contact-inquiry-section" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Submit Application Inquiry" successMessage="Thank you. Your inquiry has been submitted to the Antech Solutions application team." errorMessage="Unable to submit your application inquiry. Please verify your details or contact us directly.">
                  <InquiryFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}